// DGTExamSimulator.jsx

import React, { Component } from 'react';
import noImage from '../../../tools/auto/no-image.jpeg';
import { inject } from 'mobx-react';
import ApiStore from '../../../api/ApiStore';
import { RingLoader } from 'react-spinners';
import { FiArrowLeft } from 'react-icons/fi';
import { motion } from 'framer-motion';
import './DGTExamSimulator.responsive.css';

@inject('store')
class DGTExamSimulator extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      questionData: null,
      currentQuestionNumber: 1,
      totalQuestions: 30,

      // Stores all necessary info so we can show a detailed final summary
      userAnswers: {},
      /*
        userAnswers: {
          [index]: {
            questionId,
            selectedAnswer: 'A',
            selectedAnswerText: 'Some text',
            isCorrect: true/false,
            correctAnswer: 'B',
            correctAnswerText: 'The real correct text',
            questionText: '...',
            explanation: '...',
            image: 'base64 or something'
          }
        }
      */

      answeringEnabled: true,
      showFinalReview: false,
      showBackButton: false, // Flag to control back button visibility

      // Timer: 30 minutes in seconds
      timeLeft: 30 * 60,
      timer: null,

      // For final summary collapsible details
      showDetails: false,
      finalScore: 0,
    };
  }

  async componentDidMount() {
    // 1) Load question #1
    await this.loadQuestion(1);

    // 2) Start the countdown
    const timer = setInterval(() => {
      this.setState(prev => {
        if (prev.timeLeft <= 1) {
          clearInterval(prev.timer);
          // Auto finalize
          this.autoFinalize();
          return { timeLeft: 0, timer: null };
        }
        return { timeLeft: prev.timeLeft - 1 };
      });
    }, 1000);
    this.setState({ timer });
  }

  componentWillUnmount() {
    if (this.state.timer) {
      clearInterval(this.state.timer);
    }
  }

  // If time runs out, forcibly finalize
  autoFinalize() {
    // This will call finalizeExam with force=true, which will set showBackButton to true
    this.finalizeExam(true);
  }

  // Convert timeLeft => mm:ss
  formatTimeLeft() {
    const { timeLeft } = this.state;
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  // Load question # questionIndex
  async loadQuestion(questionIndex) {
    this.setState({
      isLoading: true,
      questionData: null,
      answeringEnabled: true,
    });
    try {
      const { studentExamId } = this.props;
      const data = await ApiStore.getNextExamQuestion(
        studentExamId,
        questionIndex
      );
      /*
        data shape:
        {
          question_id,
          text,
          explanation,
          options,         // { a: "...", b: "..." }
          correct_answer,  // e.g. "a"
          image_data
        }
      */

      const {
        question_id,
        text,
        explanation,
        options,
        correct_answer,
        image_data,
      } = data;

      // Build array of answers
      const answers = Object.entries(options)
        .filter(([, val]) => val)
        .map(([key, val]) => ({
          letter: key.toUpperCase(),
          text: val,
        }));

      const imageSrc = image_data
        ? `data:image/png;base64,${image_data}`
        : noImage;

      this.setState({
        isLoading: false,
        currentQuestionNumber: questionIndex,
        questionData: {
          questionId: question_id,
          question: text,
          explanation,
          correctAnswer: correct_answer,
          image: imageSrc,
          answers,
        },
        answeringEnabled: true,
      });
    } catch (error) {
      console.error('Error loading question:', error);
      this.setState({
        isLoading: false,
        questionData: null,
        answeringEnabled: false,
      });
    }
  }

  // When user clicks an answer
  handleOptionSelect(answer) {
    if (!this.state.answeringEnabled) return;
    const { currentQuestionNumber, questionData, userAnswers } = this.state;
    const { studentExamId } = this.props;

    // Check correctness locally
    const isCorrect =
      answer.letter === (questionData.correctAnswer || '').toUpperCase();

    // Also get the correctAnswer's text for final summary
    const correctAnswerLetter = (
      questionData.correctAnswer || ''
    ).toUpperCase();
    const correctObj = questionData.answers.find(
      ans => ans.letter === correctAnswerLetter
    );

    // We'll keep everything needed for final review
    const updatedUserAnswers = {
      ...userAnswers,
      [currentQuestionNumber]: {
        questionId: questionData.questionId,
        selectedAnswer: answer.letter,
        selectedAnswerText: answer.text,
        isCorrect,
        correctAnswer: correctAnswerLetter,
        correctAnswerText: correctObj ? correctObj.text : '',
        questionText: questionData.question,
        explanation: questionData.explanation,
        image: questionData.image, // so we can show it at final summary
      },
    };

    this.setState(
      {
        userAnswers: updatedUserAnswers,
        // Keep answeringEnabled true to allow changing answers
        answeringEnabled: true,
      },
      async () => {
        try {
          await ApiStore.submitExamAnswer(
            studentExamId,
            questionData.questionId,
            answer.letter,
            false // we don't pass actual correctness to not reveal
          );
        } catch (err) {
          console.error('Error submitting answer:', err);
        }
      }
    );
  }

  async goToNextQuestion() {
    const { currentQuestionNumber, totalQuestions } = this.state;
    if (currentQuestionNumber < totalQuestions) {
      await this.loadQuestion(currentQuestionNumber + 1);
    } else {
      alert('Has llegado a la última pregunta. Puedes finalizar el test.');
    }
  }

  goToQuestion(num) {
    this.loadQuestion(num);
  }

  // If user clicks "Finalizar Test"
  async finalizeExam(force = false) {
    if (!force) {
      const confirmEnd = window.confirm(
        '¿Estás seguro de finalizar el test ahora?'
      );
      if (!confirmEnd) return;
    }

    // 1) Calc final score
    const { userAnswers, timer } = this.state;
    const answeredCount = Object.keys(userAnswers).length;
    const correctCount = Object.values(userAnswers).filter(
      ans => ans.isCorrect
    ).length;
    const finalScore =
      answeredCount > 0 ? Math.round((correctCount / answeredCount) * 100) : 0;

    // 2) Finalize on backend
    const { studentExamId } = this.props;
    try {
      await ApiStore.finalizeExamAttempt(studentExamId, finalScore);
      // Stop timer
      if (timer) {
        clearInterval(timer);
      }
      // Show final review and enable back button
      this.setState({
        showFinalReview: true,
        finalScore,
        showBackButton: true, // Enable back button after finalizing exam
      });
    } catch (err) {
      console.error('Error finalizing exam:', err);
    }
  }

  // If time runs out
  autoFinalize() {
    this.finalizeExam(true);
  }

  // Toggle "showDetails"
  toggleDetails() {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  }

  // Final summary
  renderFinalReview() {
    const { totalQuestions, userAnswers, finalScore, showDetails } = this.state;

    // Some stats
    const answeredCount = Object.keys(userAnswers).length;
    const correctCount = Object.values(userAnswers).filter(
      ans => ans.isCorrect
    ).length;
    const incorrectCount = answeredCount - correctCount;

    // Build array for "grid of correct/incorrect"
    const questionStates = Array.from({ length: totalQuestions }, (_, i) => {
      const idx = i + 1;
      const info = userAnswers[idx];
      if (!info) {
        return { number: idx, state: 'unanswered' };
      }
      return { number: idx, state: info.isCorrect ? 'correct' : 'incorrect' };
    });

    // For the detailed breakdown
    const sortedKeys = Object.keys(userAnswers)
      .map(k => parseInt(k, 10))
      .sort((a, b) => a - b);

    return (
      <div className="results-container">
        {/* Back button - fixed position, only shown after finalizing exam */}
        {this.state.showBackButton && (
          <motion.button
            className="back-button"
            onClick={() => this.props.onClose && this.props.onClose()}
            aria-label="Volver al dashboard"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <FiArrowLeft size={18} />
          </motion.button>
        )}

        {/* Header */}
        <div className="results-header">
          <img src="/ministerio_interior_logo.gif" alt="Logo" />
        </div>

        {/* Score + stats */}
        <div className="results-content">
          <h2>Resultados del Test</h2>
          <h3 style={{ marginTop: '5px', color: '#0B3B75' }}>
            Puntuación Final: {finalScore}%
          </h3>

          {/* Grid of squares */}
          <div className="results-questions-grid">
            {questionStates.map(q => (
              <div key={q.number} className={`results-question-box ${q.state}`}>
                {String(q.number).padStart(2, '0')}
              </div>
            ))}
          </div>

          {/* Stats row */}
          <div className="results-stats">
            <div>Total Preguntas: {totalQuestions}</div>
            <div>Contestadas: {answeredCount}</div>
            <div>Correctas: {correctCount}</div>
            <div>Erróneas: {incorrectCount}</div>
          </div>

          {/* Button to expand/collapse details */}
          <button
            className="results-toggle-button"
            onClick={() => this.toggleDetails()}
          >
            {showDetails ? 'Ocultar Detalles' : 'Ver Detalles'}
          </button>

          {/* The collapsible section */}
          {showDetails && (
            <div className="results-details">
              {sortedKeys.map(idx => {
                const info = userAnswers[idx];
                const wasCorrect = info.isCorrect;
                return (
                  <div key={idx} className="results-question-detail">
                    <h4 style={{ margin: '5px 0' }}>
                      Pregunta {String(idx).padStart(2, '0')}
                    </h4>

                    {/* If we have an image for this question */}
                    {info.image && info.image !== noImage && (
                      <div style={{ marginBottom: '10px' }}>
                        <img
                          src={info.image}
                          alt="Question Img"
                          className="results-question-image"
                        />
                      </div>
                    )}

                    <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
                      {info.questionText}
                    </p>

                    {/* Show user's selected answer text */}
                    <p
                      style={{
                        margin: '2px 0',
                        color: wasCorrect ? 'green' : 'red',
                      }}
                    >
                      Tu Respuesta: ({info.selectedAnswer}){' '}
                      {info.selectedAnswerText}
                      {wasCorrect ? ' ✓' : ' ✗'}
                    </p>

                    {/* If user was wrong, show the correct text too */}
                    {!wasCorrect && (
                      <p style={{ margin: '2px 0', color: 'green' }}>
                        Correcta: ({info.correctAnswer}){' '}
                        {info.correctAnswerText}
                      </p>
                    )}

                    {/* Explanation */}
                    <div
                      style={{
                        marginTop: '5px',
                        fontStyle: 'italic',
                        fontSize: '90%',
                      }}
                    >
                      <strong>Explicación:</strong> {info.explanation || 'N/A'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  renderExamInterface() {
    const {
      isLoading,
      questionData,
      currentQuestionNumber,
      totalQuestions,
      userAnswers,
      answeringEnabled,
    } = this.state;

    if (isLoading) {
      return (
        <div className="exam-container">
          {/* Back button - fixed position, only shown after finalizing exam */}
          {this.state.showBackButton && (
            <motion.button
              className="back-button"
              onClick={() => this.props.onClose && this.props.onClose()}
              aria-label="Volver al dashboard"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <FiArrowLeft size={18} />
            </motion.button>
          )}

          <div className="exam-header">
            <img
              src="/ministerio_interior_logo.gif"
              alt="Logo"
              className="exam-logo"
            />
            <div className="exam-timer">
              Tiempo restante: {this.formatTimeLeft()}
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '50px' }}>
            <RingLoader color="#000000" size="120px" />
          </div>
        </div>
      );
    }

    if (!questionData) {
      return (
        <div className="exam-container">
          {/* Back button - fixed position, only shown after finalizing exam */}
          {this.state.showBackButton && (
            <motion.button
              className="back-button"
              onClick={() => this.props.onClose && this.props.onClose()}
              aria-label="Volver al dashboard"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <FiArrowLeft size={18} />
            </motion.button>
          )}

          <div className="exam-header">
            <img
              src="/ministerio_interior_logo.gif"
              alt="Logo"
              className="exam-logo"
            />
            <div className="exam-timer">
              Tiempo restante: {this.formatTimeLeft()}
            </div>
          </div>
          <div>No hay preguntas disponibles.</div>
        </div>
      );
    }

    // If we previously selected an answer for this question
    const prevSelected =
      userAnswers[currentQuestionNumber]?.selectedAnswer || null;

    return (
      <div className="exam-container">
        {/* Back button - fixed position, only shown after finalizing exam */}
        {this.state.showBackButton && (
          <motion.button
            className="back-button"
            onClick={() => this.props.onClose && this.props.onClose()}
            aria-label="Volver al dashboard"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <FiArrowLeft size={18} />
          </motion.button>
        )}

        <div className="exam-header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/ministerio_interior_logo.gif"
              alt="Logo"
              className="exam-logo"
            />
          </div>
          {/* Timer */}
          <div className="exam-timer">
            Tiempo restante: {this.formatTimeLeft()}
          </div>
        </div>

        <div className="exam-content">
          {/* Image */}
          <div className="exam-image-container">
            <img
              src={questionData.image}
              alt="Question"
              className="exam-image"
            />
          </div>

          {/* Q + Answers */}
          <div className="exam-question-answers">
            <div className="exam-question-header">
              <div className="exam-question-number">
                {currentQuestionNumber}.
              </div>
              <div className="exam-question-text">{questionData.question}</div>
            </div>

            <div className="exam-answers">
              {questionData.answers.map((answer, idx) => {
                const isSelected = prevSelected === answer.letter;
                return (
                  <div
                    key={idx}
                    onClick={() => this.handleOptionSelect(answer)}
                    className={`exam-answer ${isSelected ? 'selected' : ''}`}
                    style={{
                      cursor: answeringEnabled ? 'pointer' : 'default',
                    }}
                  >
                    <strong>{answer.letter})</strong> {answer.text}
                  </div>
                );
              })}
            </div>

            {/* Buttons */}
            <div className="exam-buttons">
              {/* Next */}
              <button
                className="exam-button"
                onClick={() => this.goToNextQuestion()}
              >
                <span style={{ marginRight: '10px', color: '#333' }}>
                  SIGUIENTE
                </span>
              </button>

              {/* Finalize */}
              <button
                className="exam-button"
                onClick={() => this.finalizeExam(false)}
              >
                <span
                  style={{
                    color: '#b58900',
                    fontSize: '20px',
                    marginRight: '10px',
                  }}
                >
                  ✔
                </span>
                <span style={{ color: '#333' }}>FINALIZAR TEST</span>
              </button>
            </div>
          </div>
        </div>

        {/* Question Nav */}
        <div className="exam-nav">
          {Array.from({ length: totalQuestions }, (_, i) => i + 1).map(num => {
            const active = num === currentQuestionNumber;
            const isAnswered = !!userAnswers[num];
            const buttonClasses = [
              'exam-nav-button',
              active ? 'active' : '',
              isAnswered ? 'answered' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <button
                key={num}
                onClick={() => this.goToQuestion(num)}
                className={buttonClasses}
              >
                {String(num).padStart(2, '0')}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  render() {
    if (this.state.showFinalReview) {
      return this.renderFinalReview();
    }
    return this.renderExamInterface();
  }
}

export default DGTExamSimulator;
